<!--
  ApprovalQueue.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).

  Left pane of the Approval Workspace. Receives the
  ``groupedByClient`` structure from the Pinia store and renders a
  client-name section header above each client's stack of rows.

  Props:
    - groups: array -- [{client_id, client, items}]
    - selectedId: number|null -- highlights the active row.
    - loading: boolean -- shows a placeholder skeleton during initial
      load; subsequent re-fetches don't dim the existing data (so
      the workspace doesn't flicker on action follow-ups).

  Emits:
    - select(id) -- queue row click; parent owns route navigation.

  Empty state copy: "No approvals match the current filter." Matter-of-
  fact; no demo references.
-->
<template>
    <div class="aw-queue" data-testid="approval-queue">
        <div
            v-if="loading && groups.length === 0"
            class="aw-queue__loading"
            data-testid="approval-queue-loading"
        >
            Loading approvals...
        </div>
        <div
            v-else-if="!groups.length"
            class="aw-queue__empty"
            data-testid="approval-queue-empty"
        >
            No approvals match the current filter.
        </div>
        <div
            v-for="group in groups"
            :key="group.client_id"
            class="aw-queue__group"
            :data-testid="`approval-queue-group-${group.client_id}`"
        >
            <div class="aw-queue__group-header">
                <span class="aw-queue__client-name">{{
                    group.client?.name || `Client ${group.client_id}`
                }}</span>
                <span class="aw-queue__group-count">{{
                    group.items.length
                }}</span>
            </div>
            <ApprovalQueueRow
                v-for="entry in group.items"
                :key="entry.id"
                :entry="entry"
                :selected="entry.id === selectedId"
                @click="$emit('select', entry.id)"
            />
        </div>
    </div>
</template>

<script>
import ApprovalQueueRow from './ApprovalQueueRow.vue'

export default {
    name: 'ApprovalQueue',
    components: { ApprovalQueueRow },
    props: {
        groups: {
            type: Array,
            required: true,
        },
        selectedId: {
            type: [Number, String, null],
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['select'],
}
</script>

<style scoped>
.aw-queue {
    display: flex;
    flex-direction: column;
    background: var(--tf-bg, #ffffff);
    border: 1px solid var(--tf-border, #e2e8f0);
    border-radius: var(--tf-radius-sm, 6px);
    overflow: hidden;
}
.aw-queue__loading,
.aw-queue__empty {
    padding: 40px 24px;
    text-align: center;
    color: var(--tf-fg-4, #5a6a82);
    font-size: 13px;
}
.aw-queue__group + .aw-queue__group {
    border-top: 4px solid var(--tf-surface-alt, #edf2f7);
}
.aw-queue__group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: var(--tf-surface-alt, #f7fafc);
    font-size: 12px;
    color: var(--tf-fg-3, #4a5568);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid var(--tf-border, #e2e8f0);
}
.aw-queue__client-name {
    font-weight: 600;
}
.aw-queue__group-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    padding: 0 6px;
    height: 18px;
    border-radius: 9px;
    background: var(--tf-fg-4, #5a6a82);
    color: #ffffff;
    font-size: 11px;
    font-weight: 600;
}
</style>
